import { c as _c } from "react/compiler-runtime";
import { VStack, Heading, Text, Box, StackProps, HeadingProps, TextProps, chakra } from '@chakra-ui/react';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import React from 'react';
const GRADIENT_TEXT_STYLES = {
  display: 'inline',
  backgroundImage: 'linear-gradient(90deg, #d97258 0%, #9858a3 100%)',
  backgroundClip: 'text',
  color: 'transparent'
} as const;
export interface GenericHeroProps {
  heading: string;
  /** Substring of `heading` to render with the brand gradient text style. */
  gradientText?: string;
  subheading?: string;
  eyebrow?: string;
  children?: React.ReactNode;
  containerProps?: StackProps;
  headingProps?: HeadingProps;
  subheadingProps?: TextProps;
  eyebrowProps?: TextProps;
}
function renderHeadingWithGradient(heading: string, gradientText?: string) {
  if (!gradientText) {
    return heading;
  }
  const index = heading.indexOf(gradientText);
  if (index === -1) {
    return heading;
  }
  const before = heading.slice(0, index);
  const after = heading.slice(index + gradientText.length);
  return <>
      {before}
      <chakra.span {...GRADIENT_TEXT_STYLES}>{gradientText}</chakra.span>
      {after}
    </>;
}

/**
 * GenericHero – A flexible Hero section for landing pages.
 * Renders a heading, subheading, and arbitrary children below.
 */
export const GenericHero: React.FC<GenericHeroProps> = t0 => {
  const $ = _c(39);
  const {
    heading,
    gradientText,
    subheading,
    eyebrow,
    children,
    containerProps,
    headingProps,
    subheadingProps,
    eyebrowProps
  } = t0;
  const {
    getBreakpointValue
  } = useBreakpointContext();
  let t1;
  if ($[0] !== getBreakpointValue) {
    t1 = getBreakpointValue([3, 4, 6]);
    $[0] = getBreakpointValue;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== getBreakpointValue) {
    t2 = getBreakpointValue([3, 4, 8]);
    $[2] = getBreakpointValue;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== getBreakpointValue) {
    t3 = getBreakpointValue([8, 12, 16, 20]);
    $[4] = getBreakpointValue;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== getBreakpointValue) {
    t4 = getBreakpointValue([2, 2, 4, 6]);
    $[6] = getBreakpointValue;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== eyebrow || $[9] !== eyebrowProps) {
    t5 = eyebrow && <Text color="colorPalette.fg/50" textStyle="labelXs" {...eyebrowProps}>{eyebrow}</Text>;
    $[8] = eyebrow;
    $[9] = eyebrowProps;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== getBreakpointValue) {
    t6 = getBreakpointValue(["3xl", "4xl", "5xl", "6xl"]);
    $[11] = getBreakpointValue;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== gradientText || $[14] !== heading) {
    t7 = renderHeadingWithGradient(heading, gradientText);
    $[13] = gradientText;
    $[14] = heading;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== headingProps || $[17] !== t6 || $[18] !== t7) {
    t8 = <Heading as="h1" fontFamily="brand" fontSize={t6} fontWeight="extrabold" lineHeight="1.1" {...headingProps}>{t7}</Heading>;
    $[16] = headingProps;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== getBreakpointValue || $[21] !== subheading || $[22] !== subheadingProps) {
    t9 = subheading && <Text color="colorPalette.fg/70" fontSize={getBreakpointValue(["md", "lg", "xl"])} fontWeight="medium" letterSpacing="tight" {...subheadingProps}>{subheading}</Text>;
    $[20] = getBreakpointValue;
    $[21] = subheading;
    $[22] = subheadingProps;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t4 || $[25] !== t5 || $[26] !== t8 || $[27] !== t9) {
    t10 = <VStack align="stretch" gap={t4}>{t5}{t8}{t9}</VStack>;
    $[24] = t4;
    $[25] = t5;
    $[26] = t8;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== children || $[30] !== getBreakpointValue) {
    t11 = children && <Box paddingTop={getBreakpointValue([1, 2, 4])} width="full">{children}</Box>;
    $[29] = children;
    $[30] = getBreakpointValue;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== containerProps || $[33] !== t1 || $[34] !== t10 || $[35] !== t11 || $[36] !== t2 || $[37] !== t3) {
    t12 = <VStack align="center" as="section" gap={t1} marginX="auto" maxWidth="container.lg" paddingX={t2} paddingY={t3} textAlign="center" {...containerProps}>{t10}{t11}</VStack>;
    $[32] = containerProps;
    $[33] = t1;
    $[34] = t10;
    $[35] = t11;
    $[36] = t2;
    $[37] = t3;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  return t12;
};
export default GenericHero;